import {
  BybHr,
  BybImageSection,
  BybLeaderboardListItem,
  BybList,
  BybListItem,
  BybListItemSkeleton,
  BybRewardListItem,
  BybSection,
  BybSectionHeader,
} from "@Components/common/byb-ds";
import BybVrCard from "@Components/common/byb-ds/cards/byb-vr-card";
import { formatTimeUntil } from "@Components/game/challenges/cards/utils";
import ChallengeDialog from "@Components/game/challenges/challenge-dialog";
import { BybBackground, BybButtonVR } from "@Components/game/common";
import SessionResultsLoadingOverlay from "@Components/game/common/session-results-loading-overlay";
import ContainerWrapper from "@Components/generic/container-wrapper";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { Actions, AppContext, Events, UnityBridge } from "@Context/common/general/context";
import ChallengeStatus from "@Enums/ChallengeStatus";
import Preferences from "@Enums/Preferences";
import RewardScarcity from "@Enums/RewardScarcity";
import { AnalyticsEvents, VRHeaderButtonType } from "@Enums/index";
import FocusIcon from "@Resources/app/icons-v2/system-icons/focus-icon";
import PlayIcon from "@Resources/app/icons-v2/system-icons/play-icon";
import { faAlarmClock, faCheck } from "@fortawesome/pro-solid-svg-icons";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { useChallengeData } from "@hooks/common/challenges/use-challenge-data";
import useUserPreferences from "@hooks/common/user/use-user-preferences";
import useCompletePlaySession from "@hooks/game/use-complete-play-session";
import useI18n from "@i18n";
import { Alert, Box, Button, Card, Container, Grid, IconButton, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { getChallengeLocizeKeyPrefix } from "./utils";

/**
 * This entire file is a mess, but don't bother refactoring it. When we get a real design for the page, just
 * start from scratch.
 */

const PageId = "CHALLENGE";
const CHALLENGE_BACKGROUND_URL = "https://challenge-thumbs.b-cdn.net/byb-mode-posters/img-challenges-wide.webp";

const Challenge = () => {
  const { state, dispatch } = useContext(AppContext);
  const { t } = useTranslation("VR");
  const { i18n } = useI18n();
  const theme = useTheme();
  const navData: any = useLocation().state || null;
  const { challengeId: challengeIdString } = useParams();
  const challengeId = +challengeIdString!;
  const [showChallengeDialog, setShowChallengeDialog] = useState(false);
  const [isResolvingChallengeResult, setIsResolvingChallengeResult] = useState(false);
  const { track } = useAnalytics();

  const {
    challengeDetails,
    leaderboardData,
    loadingLeaderboard,
    loadingLeaderboardVisibility,
    leaderboardError,
    retryLeaderboard,
    userLeaderboardData,
    challengeResults,
    loadingChallengeResults,
    challengeResultsError,
    retryChallengeResults,
    achievedRewards,
    latestResult,
    latestReward,
    userInShortList,
    leaderboardVisibility,
    fetchLeaderboard,
    fetchUserLeaderboard,
    onChallengeResultsFetched,
  } = useChallengeData({ id: challengeId, overrideData: navData });

  const { updatePreferences } = useUserPreferences();

  const [showSpecialDialog, setShowSpecialDialog] = useState(false);
  const specialReward = challengeDetails?.rewards?.find((r: any) => r.type === "SPECIAL");

  const hasEnded = Boolean(challengeDetails && challengeDetails.end_at && new Date(challengeDetails.end_at).getTime() < Date.now());
  const hasNotStarted = Boolean(challengeDetails && challengeDetails.start_at && new Date(challengeDetails.start_at).getTime() > Date.now());

  const locizeKeyPrefix = getChallengeLocizeKeyPrefix(challengeDetails);
  const hasUserPlayedChallenge = (challengeResults?.challenge_sessions.length ?? 0) > 0;

  const startSession = () => {
    dispatch({
      type: Actions.SessionActions.CreateChallengeSession,
      payload: {
        account_id: state.account?.id || null,
        challenge_id: challengeDetails!.id,
      },
      onSuccess: onChallengeSessionCreated,
    });
  };

  const onChallengeSessionCreated = (response: any) => {
    track(AnalyticsEvents.challenge_started, { challenge_id: challengeId });
    UnityBridge.dispatchWebEvent({
      type: UnityBridge.OutgoingWebEvents.StartPlaylist,
      payload: {
        items: response.play_items,
        play_session_id: response.play_session_id,
      },
    });
  };

  const navigateToSessionResults = (playSessionId: number) => {
    Events.app.navigate.emit({
      path: `${location.pathname}/sessions/session/${playSessionId}`,
      data: {
        tags: challengeDetails?.tags,
      },
    });
  };

  const { isCompletingSession } = useCompletePlaySession({
    delayMs: 500,
    onSuccess: () => {
      setIsResolvingChallengeResult(true);
      dispatch({ type: Actions.UsersActions.GetAccountInfo });
      fetchLeaderboard();
      fetchUserLeaderboard();
      dispatch({
        type: Actions.ChallengesActions.GetChallengeResults,
        payload: {
          user_id: state.user.id,
          challenge_id: challengeId,
        },
        onSuccess: results =>
          onChallengeResultsFetched(results, () => {
            if (challengeResults?.challenge_sessions.length === 0 && specialReward) {
              setShowSpecialDialog(true);
            }
            setIsResolvingChallengeResult(false);
            setShowChallengeDialog(true);
          }),
      });
    },
  });

  const joinLeaderboard = () => {
    updatePreferences([{ id: Preferences.leaderboard_visibility, value: true }], {
      onSuccess: () => fetchLeaderboard(true),
    });
    track(AnalyticsEvents.join_leaderboard_clicked, {});
  };

  const showVideo = (videoId?: string | null) => {
    if (videoId)
      state.app.events.showVideo.emit({
        videoId: videoId,
      });
  };

  useEffect(() => {
    state.app.events.showHeaderButton.emit({ type: VRHeaderButtonType.BACK, title: i18n("challenges.challenge.page.title") });
    return () => {
      state.app.events.hideHeaderButton.emit();
    };
  }, []);

  useEffect(() => {
    // Auto play video if there are no results and there is a video for the challenge
    if (challengeResults && challengeResults.challenge_sessions.length === 0 && challengeDetails?.video_url) {
      showVideo(challengeDetails.video_url);
    }
  }, [challengeResults, challengeDetails]);

  return (
    <ContainerWrapper id={PageId} fullHeight sx={{ overflow: "hidden", position: "relative" }}>
      <BybBackground imageUrl={CHALLENGE_BACKGROUND_URL} />

      <Container maxWidth={false} sx={{ height: "100%", padding: theme.spacing(12, 0, 3) }}>
        <Grid container spacing={2} sx={{ height: `calc(100% + ${theme.spacing(2)})` }}>
          {/**
           * *************************
           * Challenge details section
           * *************************
           */}
          <Grid item xs={7} sx={{ display: "flex", height: "100%", minHeight: 0 }}>
            <BybVrCard sx={{ overflowY: "auto", justifyContent: "flex-start" }}>
              <Stack direction="column" flexGrow={1}>
                <BybImageSection imgSrc={challengeDetails?.image_url} imgSize="large">
                  {/* Video */}
                  {challengeDetails?.video_url && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "220px", // Same height as the image in a "large" BybImageSection
                        zIndex: 1,
                        background: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <HapticsButtonWrapper>
                        <IconButton onClick={() => showVideo(challengeDetails.video_url)}>
                          <PlayIcon size={64} color="#FFF" />
                        </IconButton>
                      </HapticsButtonWrapper>
                    </Box>
                  )}
                </BybImageSection>

                <Stack direction="column" flexGrow={1}>
                  <Stack
                    direction="row"
                    sx={{
                      textAlign: "center",
                      alignItems: "center",
                      padding: theme.spacing(0, 2.5),
                    }}
                    spacing={1.5}
                  >
                    <Box sx={{ padding: theme.spacing(4, 0), width: "40%" }}>
                      <BybSectionHeader
                        title={t(`${locizeKeyPrefix}.name`, `${challengeDetails?.title}`)}
                        subTitle={t(`${locizeKeyPrefix}.caption`, `${challengeDetails?.subtitle}`)}
                      />
                    </Box>
                    <Box sx={{ padding: theme.spacing(1, 0), width: "60%" }}>
                      {challengeDetails ? (
                        <Stack direction="row" spacing={3.5} alignItems="center" paddingX={4}>
                          <Box display="flex" alignItems="center" justifyContent="center">
                            <FocusIcon color={theme.palette.dsNeutral.v400} />
                          </Box>
                          <Typography variant="textBase400" whiteSpace="pre-wrap" color="dsBase.v200" textAlign="left">
                            {t(`${locizeKeyPrefix}.goal`, `${challengeDetails.goal}`)}
                          </Typography>
                        </Stack>
                      ) : (
                        <BybListItemSkeleton avatar />
                      )}
                      {challengeDetails && (hasEnded || challengeDetails.end_at) && (
                        <BybListItem
                          header={
                            hasEnded
                              ? i18n("challenges.challenge.page.has-ended")
                              : i18n("challenges.challenge.page.ends-in", {
                                  time: formatTimeUntil(challengeDetails.end_at || null, i18n),
                                })
                          }
                          icon={<Icon icon={hasEnded ? faCheck : faAlarmClock} fontSize={22} />}
                        />
                      )}
                      {hasNotStarted && (
                        <BybListItem header={i18n("challenges.challenge.page.not-started")} icon={<Icon icon={faAlarmClock} fontSize={22} />} />
                      )}
                    </Box>
                  </Stack>
                  <BybSection
                    title={i18n("challenges.challenge.page.description")}
                    sx={{
                      background: "transparent",
                    }}
                  >
                    <Box sx={{ padding: theme.spacing(1.25, 2.5) }}>
                      {challengeDetails ? (
                        <Typography variant="textBase400" whiteSpace="pre-wrap" color="dsBase.v200">
                          {t(`${locizeKeyPrefix}.description`, `${challengeDetails.description}`)}
                        </Typography>
                      ) : (
                        <Skeleton variant="rounded" width="100%" height={theme.spacing(12)} />
                      )}
                    </Box>
                  </BybSection>
                  <Stack sx={{ flexGrow: 1, justifyContent: "center", alignItems: "center" }}>
                    <Button onClick={startSession} sx={{ padding: theme.spacing(2.5, 8) }} disabled={hasEnded || hasNotStarted}>
                      {i18n("start")}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </BybVrCard>
          </Grid>

          <Grid item xs={5} sx={{ height: "100%", minHeight: 0 }}>
            <Stack spacing={2} sx={{ height: "100%", minHeight: 0 }}>
              {/**
               * *************************
               * Results section
               * *************************
               */}
              <Card sx={{ height: "auto", padding: theme.spacing(3.5) }}>
                <BybSection
                  title={i18n("challenges.challenge.page.results.title")}
                  tight
                  sx={{
                    flexGrow: 1,
                    background: "transparent",
                  }}
                >
                  {challengeResultsError ? (
                    <Alert
                      severity="error"
                      action={
                        <Button color="inherit" onClick={retryChallengeResults}>
                          {i18n("button.retry")}
                        </Button>
                      }
                    >
                      {i18n("challenges.challenge.page.results.error.description")}
                    </Alert>
                  ) : loadingChallengeResults ? (
                    <Skeleton variant="rounded" height={120} />
                  ) : (
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={2} sx={{ padding: theme.spacing(0, 2, 1, 2) }}>
                        {challengeDetails?.rewards
                          ?.filter(r => r.type === "TROPHY")
                          .map((r, index) => (
                            <Fragment key={r.id}>
                              <BybRewardListItem
                                key={r.id}
                                id={r.id}
                                name={t(r.name)}
                                goal={challengeDetails.goal}
                                type={r.type}
                                scarcity={r.scarcity}
                                challengeId={challengeDetails.id}
                                row
                                earnedAt={
                                  achievedRewards.find(ar => {
                                    return ar.id === r.id;
                                  })?.created_at
                                }
                                bestResultIq={r.best_result_iq}
                                userRank={r.user_rank}
                                rewardPercentage={r.reward_percentage}
                              />
                              {index < challengeDetails.rewards.filter(r => r.type === "TROPHY").length - 1 && <BybHr />}
                            </Fragment>
                          )) ??
                          [...Array(3)].map((_, index) => (
                            <Fragment key={index}>
                              <Box padding={theme.spacing(2.5)}>
                                <Skeleton
                                  variant="rounded"
                                  sx={{
                                    height: theme.spacing(8),
                                    width: theme.spacing(8),
                                    flexShrink: 0,
                                  }}
                                />
                              </Box>
                              {index < 2 && <BybHr />}
                            </Fragment>
                          ))}
                      </Stack>
                      {hasUserPlayedChallenge && (
                        <Stack direction="row" sx={{ justifyContent: "flex-end", paddingX: theme.spacing(2.5) }}>
                          <BybButtonVR
                            color="secondary"
                            size="small"
                            onClick={() => {
                              Events.app.navigate.emit({
                                path: `${location.pathname}/sessions`,
                                data: {
                                  play_session_ids: challengeResults?.challenge_sessions.map(session => session.play_session.id),
                                },
                              });

                              if (challengeDetails) {
                                track(AnalyticsEvents.challenge_see_all_results_clicked, {
                                  challengeId: challengeDetails.id,
                                  title: challengeDetails.title,
                                });
                              }
                            }}
                            disabled={challengeResults?.challenge_sessions.length === 0}
                          >
                            {i18n("challenges.challenge.page.results.see-all")}
                          </BybButtonVR>
                        </Stack>
                      )}
                    </Stack>
                  )}
                </BybSection>
              </Card>

              {/**
               * *************************
               * Leaderboard section
               * *************************
               */}
              <Card sx={{ display: "flex", flex: 1, padding: theme.spacing(3.5) }}>
                <BybSection
                  title={i18n("challenges.challenge.page.leaderboard.top", { count: 10 })}
                  valueHeader={i18n("bybiq.label")}
                  tight
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    minHeight: 0,
                    background: "transparent",
                  }}
                >
                  {!loadingLeaderboard &&
                    !loadingLeaderboardVisibility &&
                    !leaderboardError &&
                    leaderboardData.length === 0 &&
                    leaderboardVisibility && (
                      <Box
                        sx={{
                          padding: theme.spacing(2.5),
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="textBase400"
                          color="dsBase.v200"
                          sx={{
                            fontStyle: "italic",
                          }}
                        >
                          {i18n("challenges.challenge.page.leaderboard.no-data")}
                        </Typography>
                      </Box>
                    )}

                  {loadingLeaderboard || loadingLeaderboardVisibility ? (
                    <BybList sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                      {[...Array(10)].map((_, i) => (
                        <BybListItemSkeleton key={i} rank avatar />
                      ))}
                    </BybList>
                  ) : leaderboardError ? (
                    <Alert
                      severity="error"
                      action={
                        <Button color="inherit" onClick={retryLeaderboard}>
                          {i18n("button.retry")}
                        </Button>
                      }
                    >
                      {leaderboardError.message}
                    </Alert>
                  ) : leaderboardVisibility ? (
                    <BybList
                      sx={{
                        flex: 1,
                        minHeight: 0,
                      }}
                    >
                      {leaderboardData && (
                        <Stack
                          sx={{
                            overflowY: "scroll",
                          }}
                        >
                          {leaderboardData.map(player => (
                            <BybLeaderboardListItem
                              key={player.doc_id}
                              user={player}
                              value={Number(player?.score?.toFixed(0))} // TODO: round BYB IQ somewhere else (backend?)
                              highlighted={player.doc_id === state.user.id}
                            />
                          ))}
                        </Stack>
                      )}
                      {userLeaderboardData && !userInShortList && (
                        <>
                          <BybHr variant="list" ellipsis />
                          <BybLeaderboardListItem
                            key={userLeaderboardData.doc_id}
                            user={userLeaderboardData}
                            value={Number(userLeaderboardData?.score?.toFixed(0))} // TODO: round BYB IQ somewhere else (backend?)
                            highlighted
                          />
                        </>
                      )}
                    </BybList>
                  ) : (
                    <Box sx={{ padding: theme.spacing(2.5) }}>
                      <BybButtonVR color="secondary" onClick={joinLeaderboard} fullWidth>
                        {i18n("challenges.challenge.page.leaderboard.join")}
                      </BybButtonVR>
                    </Box>
                  )}
                </BybSection>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        {latestResult &&
          (showSpecialDialog && specialReward ? (
            /**
             * This is meant to only support the bootcamp challenge, which is not possible to fail.
             * For future special challenges, this WILL need to be adjusted.
             */
            <ChallengeDialog
              open={true}
              status={ChallengeStatus.COMPLETED}
              rewardName={specialReward.name}
              rewardScarcity={specialReward.scarcity as RewardScarcity}
              rewardImageUrl={specialReward.image_url}
              onContinue={() => {
                setShowChallengeDialog(false);
                setShowSpecialDialog(false);
                navigateToSessionResults(latestResult.play_session.id);
              }}
            />
          ) : (
            <ChallengeDialog
              open={showChallengeDialog}
              status={latestResult.status as ChallengeStatus}
              rewardName={latestReward?.name}
              rewardScarcity={latestReward?.scarcity as RewardScarcity}
              onContinue={() => {
                setShowChallengeDialog(false);
                navigateToSessionResults(latestResult.play_session.id);
              }}
            />
          ))}
      </Container>

      <SessionResultsLoadingOverlay open={isCompletingSession || isResolvingChallengeResult} />
    </ContainerWrapper>
  );
};

export default Challenge;
